import { useState, useEffect, useRef, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, Pause, RotateCcw, Film, X } from 'lucide-react';
import { fmtWhen } from './rmmUtils';

// Plays back an encrypted remote-session recording. Frames are fetched
// (auth'd) as blobs, decrypted server-side, and rendered on a canvas in
// recorded time order.
export const RecordingPlayer = ({ recording, onClose }) => {
  const [manifest, setManifest] = useState(null);
  const [frames, setFrames] = useState([]);      // {t, url}
  const [loaded, setLoaded] = useState(0);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const urls = [];
    (async () => {
      const { data } = await rmmAPI.remoteRecordingManifest(recording.id);
      if (cancelled) return;
      setManifest(data);
      const collected = [];
      for (let i = 0; i < data.frames.length; i++) {
        try {
          const res = await rmmAPI.remoteRecordingFrame(recording.id, i);
          const url = URL.createObjectURL(res.data);
          urls.push(url);
          collected.push({ t: data.frames[i].t, url });
          if (!cancelled) setLoaded(i + 1);
        } catch { /* skip bad frame */ }
      }
      if (!cancelled) { setFrames(collected); drawFrame(0, collected); }
    })();
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording.id]);

  const drawFrame = (i, list = frames) => {
    const f = list[i]; if (!f) return;
    const img = new Image();
    img.onload = () => {
      const c = canvasRef.current; if (!c) return;
      if (c.width !== img.width) { c.width = img.width; c.height = img.height; }
      c.getContext('2d').drawImage(img, 0, 0);
    };
    img.src = f.url;
  };

  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    let next = idx;
    while (next < frames.length - 1 && frames[next + 1].t <= elapsed) next += 1;
    if (next !== idx) { setIdx(next); drawFrame(next); }
    if (next >= frames.length - 1) { setPlaying(false); return; }
    rafRef.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, frames]);

  useEffect(() => {
    if (playing) {
      startRef.current = Date.now() - (frames[idx]?.t || 0);
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const seek = (i) => { setPlaying(false); setIdx(i); drawFrame(i); };
  const restart = () => { seek(0); setPlaying(true); };
  const total = manifest?.frames?.length || 0;
  const loading = frames.length === 0 || loaded < total;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] p-0 bg-slate-900 border-slate-700 overflow-hidden" data-testid="recording-player-dialog">
        <DialogTitle className="sr-only">Session recording of {recording.device_hostname}</DialogTitle>
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 text-slate-100">
          <div className="flex items-center gap-2 text-sm">
            <Film className="w-4 h-4 text-violet-400" />
            <span className="font-semibold">{recording.device_hostname}</span>
            <Badge className="bg-violet-500/20 text-violet-300 text-[10px]">encrypted recording</Badge>
            <span className="text-slate-400 text-xs">{recording.technician_name} · {fmtWhen(recording.created_at)}</span>
          </div>
          <Button size="sm" variant="destructive" className="h-7" onClick={onClose} data-testid="recording-close-btn"><X className="w-4 h-4" /></Button>
        </div>
        <div className="relative bg-black flex items-center justify-center" style={{ minHeight: '55vh' }}>
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 gap-3 z-10">
              <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
              <p className="text-sm" data-testid="recording-loading">Decrypting frames… {loaded}/{total || '…'}</p>
            </div>)}
          <canvas ref={canvasRef} className="max-w-full max-h-[70vh]" data-testid="recording-canvas" />
        </div>
        <div className="px-4 py-2 bg-slate-800 flex items-center gap-3">
          {playing
            ? <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700" onClick={() => setPlaying(false)} data-testid="recording-pause-btn"><Pause className="w-4 h-4" /></Button>
            : <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700" disabled={loading || frames.length === 0} onClick={() => (idx >= frames.length - 1 ? restart() : setPlaying(true))} data-testid="recording-play-btn"><Play className="w-4 h-4" /></Button>}
          <Button size="sm" variant="ghost" className="h-8 text-slate-200" onClick={restart} disabled={frames.length === 0} data-testid="recording-restart-btn"><RotateCcw className="w-4 h-4" /></Button>
          <input type="range" min={0} max={Math.max(0, frames.length - 1)} value={idx} onChange={(e) => seek(Number(e.target.value))}
            className="flex-1 accent-violet-500" disabled={frames.length === 0} data-testid="recording-seek" />
          <span className="text-xs text-slate-400 tabular-nums" data-testid="recording-frame-counter">{idx + 1}/{frames.length || total}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
